import { useEffect } from 'react'
import {
  IMarketplaceProductPromotion,
  IPercentageRange,
  IProductPriceRange,
} from '@scentregroup/shared/types'
import {
  MarketplaceProduct,
  IMarketplaceProductVariant,
  IssueType,
  IIssueWithMessage,
} from '../../types/marketplace-product'
import { createNameFromId } from '../../components/input-modules'
import { OptionType } from '../../types/product-form-inputs'
import trackMkpEvent, { labels } from '../../lib/market-place-track-event'
import {
  IAddToCartMutationResult,
  IMarketplaceCartItem,
  isIAddToCartMutationResult,
  IAddToCartResponse,
  getCartItemMkpCount,
} from '../../types/cart'
import { IUseFormReturnType } from '../../form/use-form'
import { IMKPProductValues, ProductFields } from '../../types/product-form'
import { OnSubmitFnArgs } from '../../components/product-form'
import makeUniqueProductId from '@scentregroup/shared/helpers/make-unique-product-id'
import { categoryToString } from '@scentregroup/shared/helpers/product-category'
import { displayMinimumPriceWithoutCurrency } from '@scentregroup/shared/helpers/product-price'
import { trackGeneralEvent } from '@scentregroup/shared/helpers/analytics-utils/track-event'
import analyticsUtils from '@scentregroup/shared/helpers/analytics-utils'

export const INITIAL_VALUES = { quantity: 1 }
const ascSort = (a: number, b: number): number => a - b

const getAddedCartItem = (
  cart: IAddToCartMutationResult,
  product: MarketplaceProduct,
  selectedVariant: IMarketplaceProductVariant | null,
  quantity: number
): IMarketplaceCartItem | null => {
  if (selectedVariant) {
    const addedCartItem = cart?.marketplaceItems.find(
      cartItem =>
        cartItem.sku === selectedVariant.sku &&
        cartItem.shop.id === product.shop.id
    )
    if (addedCartItem) {
      return {
        ...addedCartItem,
        quantity,
      }
    }
  }
  return null
}

export const getInitialOptionValues = (
  product: MarketplaceProduct
): Record<string, string> | null =>
  product.options
    .filter(option => option.values.length === 1)
    .reduce(
      (options, currentOption) => ({
        ...options,
        [createNameFromId(currentOption.id, OptionType.OPTION)]:
          currentOption.values[0].id,
      }),
      {}
    )

export function priceRange(
  variants: IMarketplaceProductVariant[]
): IProductPriceRange | undefined {
  const originalPrices = variants
    .flatMap(pv => pv.originalPrice ?? [])
    .sort(ascSort)
  if (!originalPrices.length) {
    return undefined
  }
  return {
    min: originalPrices[0],
    max: originalPrices[originalPrices.length - 1],
  }
}

export function discountedPriceRange(
  variants: IMarketplaceProductVariant[]
): IProductPriceRange | undefined {
  const originalPrices = variants
    .flatMap(pv => pv.originalPrice ?? [])
    .sort(ascSort)
  const effectivePrices = variants
    .flatMap(pv => (pv.discountPrice || pv.originalPrice) ?? [])
    .sort(ascSort)

  const noDiscountedPriceRangeExists =
    (originalPrices[0] === effectivePrices[0] &&
      originalPrices[originalPrices.length - 1] ===
        effectivePrices[effectivePrices.length - 1]) ||
    (!originalPrices.length && !effectivePrices.length)
  if (noDiscountedPriceRangeExists) {
    return undefined
  }

  return {
    min: effectivePrices[0],
    max: effectivePrices[effectivePrices.length - 1],
  }
}

export function discountPercentageRange(
  variants: IMarketplaceProductVariant[]
): IPercentageRange | undefined {
  const variantDiscounts = variants
    .flatMap(pv => {
      const originalPrice = pv.originalPrice
      const effectivePrice = pv.discountPrice || pv.originalPrice
      if (!originalPrice || !effectivePrice) {
        return []
      }
      const percentage =
        ((originalPrice - effectivePrice) / originalPrice) * 100
      return Math.round(percentage - 0.9 + 0.5) // set the threshold for rounding to x.9%
    })
    .sort(ascSort)

  if (variantDiscounts[variantDiscounts.length - 1] == 0) {
    return undefined
  }

  return {
    min: Math.floor(variantDiscounts[0]),
    max: Math.floor(variantDiscounts[variantDiscounts.length - 1]),
  }
}

export const isProductReturnable = (
  variants: IMarketplaceProductVariant[]
): boolean | undefined => {
  if (variants.every(v => v.returnable === true)) {
    return true
  }
  if (variants.every(v => v.returnable === false)) {
    return false
  }
  return undefined
}

export function commonApplicablePromotions(
  variants: Pick<IMarketplaceProductVariant, 'promotions'>[]
): IMarketplaceProductPromotion[] {
  function allVariantsHavePromotion({
    campaignId,
    title,
  }: IMarketplaceProductPromotion): boolean {
    return variants.every(
      variant =>
        variant?.promotions?.find(
          promotion =>
            promotion.campaignId === campaignId && promotion.title === title
        )
    )
  }

  return variants[0]?.promotions?.filter(allVariantsHavePromotion) ?? []
}

export const updateUrlWithVariant = (variantSku: string | null): void => {
  const { as } = window.history.state
  const url = replaceVariantParam(as, variantSku)
  window.history.replaceState(window.history.state, '', url)
}

export const replaceVariantParam = (
  originalPath: string,
  newVariantSku: string | null
): string => {
  const url = new URL(originalPath, window.location.origin)

  if (newVariantSku) {
    url.searchParams.set('v', newVariantSku)
  } else {
    url.searchParams.delete('v')
  }

  return url.toString()
}

export const onProductSubmitted = (
  cartId: string | undefined,
  addToCartData: IAddToCartResponse,
  setCartId: (cartId: string) => void,
  setCartCount: (count: string) => void,
  setAddedCartItem: (result: IMarketplaceCartItem) => void,
  form: IUseFormReturnType<IMKPProductValues, OnSubmitFnArgs>,
  product: MarketplaceProduct,
  selectedVariant: IMarketplaceProductVariant | null
): void => {
  if (isIAddToCartMutationResult(addToCartData.addProductToCart)) {
    if (addToCartData.addProductToCart.id !== cartId) {
      setCartId(addToCartData.addProductToCart.id)
    }

    setCartCount(getCartItemMkpCount(addToCartData.addProductToCart))

    const cartItem = getAddedCartItem(
      addToCartData.addProductToCart,
      product,
      selectedVariant,
      Number(form.values.quantity) || 0
    )

    if (cartItem) {
      setAddedCartItem(cartItem)
    }

    trackGeneralEvent(analyticsUtils.events.ADD_TO_CART_COMPLETE)

    form.handleBlur({ name: ProductFields.QUANTITY, value: 1 })
  }
}

export const onSubmit = (
  values: IMKPProductValues,
  mutation: OnSubmitFnArgs[0],
  createMutationInput: OnSubmitFnArgs[1]
): void => {
  const mutationInput = createMutationInput(values)
  mutation({ variables: mutationInput })
}

export const useTrackViewOutOfStockError = (
  productTitle: string,
  productIssue: IIssueWithMessage | null
): void => {
  useEffect(() => {
    if (productIssue?.type === IssueType.OutOfStock) {
      trackMkpEvent.viewed(labels.createOutOfStockProduct(productTitle))
    }
  }, [productIssue?.type, productTitle])
}

export const createTrackable = (
  product: MarketplaceProduct,
  selectedVariant: IMarketplaceProductVariant | null,
  variantGroupCode: string,
  price?: IProductPriceRange
): Record<string, string | undefined> => {
  return {
    'product-ecomm-detail': 'product',
    'data-ecomm-product': 'product',
    'data-ecomm-brand': product.brand,
    'data-product-retailer': product.shop.name,
    'data-product-retailer-id': product.shop.retailerId ?? '',
    'data-product-category': categoryToString(product.category) ?? '',
    'data-product-id': variantGroupCode,
    'data-product-unique-id': makeUniqueProductId(
      product.id,
      product.shop.id,
      selectedVariant?.sku
    ),
    'data-product-name': product.title,
    'data-product-price': price && displayMinimumPriceWithoutCurrency(price),
    'data-product-list': 'featured products',
    'data-product-variant':
      selectedVariant?.optionValues.map(o => o.value).join('|') || '',
    'data-product-variant-sku': selectedVariant?.sku,
  }
}
